var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// Mimic RNA-FISH's EditKitForm, as we have the same problems to solve here.
// Should be inside a `Form` whose `error` prop is set appropriately.
import * as React from "react";
import { Form, Grid, Header, Table } from "semantic-ui-react";
import ErrorDisplay from "../../ErrorDisplay";
import { Help } from "../../Help";
import { postJsonWithAuthn } from "../../RequestHelpers";
import { buildOnComponentStateChangeHandler } from "../FormState";
import { defaultAmpState, defaultSampleTypeState, discountMessage, priceColumn, PriceDisplay, ToggleableKitComponent } from "../rna-fish/EditKitForm";
import { HcrAmplifierForm } from "../rna-fish/HcrAmplifierForm";
import KitSerializer, { serializePriceMode } from "../rna-fish/KitSerializer";
import { ErrorElementsWithPrices } from "../rna-fish/NewRnaFishKitPage";
import SelectSampleTypeForm from "../rna-fish/SelectSampleTypeForm";
import BufferForm from "./BufferForm";
import { getSecondaryIfAmplifierCatalog, getSecondaryIfProbeCatalog } from "./SecondaryIfCatalog";
import { serializeBuffers, serializeProbe } from "./KitSerialization";
import { defaultBuffersState, defaultProbeState } from "./NewSecondaryIfKitPage";
import ProbeForm from "./ProbeForm";
var EditKitForm = /** @class */ (function (_super) {
    __extends(EditKitForm, _super);
    function EditKitForm(props) {
        var _this = _super.call(this, props) || this;
        // Doesn't handle setting the amplifier component's amplifier based on the probe amplifier, so this is wrapped
        _this.onProbeStateChangeBaseHandler = buildOnComponentStateChangeHandler(probeStateWrangler, _this.props.setFormState);
        _this.onAmpStateChangeBaseHandler = buildOnComponentStateChangeHandler(ampStateWrangler, _this.props.setFormState);
        // The amplifier section owns the amplifier choice, so an amp change re-points the probe's
        // pairing (probeState.probeAmplifier) at the matching entry for the currently selected probe.
        // When the pairing already matches (as it does when the change came from onProbeStateChange)
        // this is a no-op, which also keeps probe→amp→probe propagation from cycling.
        _this.onAmpStateChange = function (update, callback) {
            return _this.onAmpStateChangeBaseHandler(update, function (newState) {
                var currentPairing = newState.probeState.probeAmplifier;
                var probe = currentPairing === undefined ? undefined :
                    getSecondaryIfProbeCatalog(_this.props.legacy, _this.props.catalog)
                        .probesByAmplifierId.get(currentPairing.id);
                var newPairing = probe === null || probe === void 0 ? void 0 : probe.amplifiers.find(function (pa) { return pa.amplifier.id === newState.ampState.hcrAmplifier.id; });
                if (newPairing !== undefined && newPairing.id !== (currentPairing === null || currentPairing === void 0 ? void 0 : currentPairing.id)) {
                    _this.onProbeStateChangeBaseHandler({ probeAmplifier: newPairing }, callback);
                }
                else if (callback !== undefined) {
                    callback(newState);
                }
            });
        };
        _this.onBufferStateChange = buildOnComponentStateChangeHandler(bufStateWrangler, _this.props.setFormState);
        _this.onSampleTypeStateChangeBaseHandler = buildOnComponentStateChangeHandler(sampleTypeStateWrangler, _this.props.setFormState);
        // When the probe is enabled the amplifier choice must stay within the amps the selected probe
        // offers (the probe section mirrors this dropdown); a standalone amplifier gets the full catalog.
        _this.ampCatalogForSelectedProbe = function () {
            var ampCatalog = getSecondaryIfAmplifierCatalog(_this.props.legacy, _this.props.catalog);
            var pairing = _this.props.formState.probeState.probeAmplifier;
            if (!_this.props.formState.probeEnabled || pairing === undefined) {
                return ampCatalog;
            }
            var probe = getSecondaryIfProbeCatalog(_this.props.legacy, _this.props.catalog)
                .probesByAmplifierId.get(pairing.id);
            if (probe === undefined) {
                return ampCatalog;
            }
            var allowedAmpIds = probe.amplifiers.map(function (pa) { return pa.amplifier.id; });
            return __assign(__assign({}, ampCatalog), { channels: ampCatalog.channels.filter(function (c) { return allowedAmpIds.indexOf(c.hcrAmplifier.id) >= 0; }) });
        };
        // Wrapper around normal state propagation to handle setting the amplifier component's amplifier
        _this.onProbeStateChange = function (probeState, prevAmplifier, callback) {
            return _this.onProbeStateChangeBaseHandler(probeState, function (newState) {
                var _a, _b;
                // TODO certain probe changes will always change the amplifier to the default amplifier. Use prevAmplifier
                // to make sure that the change to default only happens when we're changing the amplifier.
                var hcrAmplifier = (_a = newState.probeState.probeAmplifier) === null || _a === void 0 ? void 0 : _a.amplifier;
                var label = (_b = getSecondaryIfAmplifierCatalog(_this.props.legacy, _this.props.catalog)
                    .channels.find(function (c) {
                    return c.hcrAmplifier.id === (hcrAmplifier === null || hcrAmplifier === void 0 ? void 0 : hcrAmplifier.id);
                })) === null || _b === void 0 ? void 0 : _b.defaultLabel.label;
                if (hcrAmplifier !== undefined && label !== undefined) {
                    _this.onAmpStateChange({
                        hcrAmplifier: hcrAmplifier,
                        label: label
                    }, callback);
                }
                else {
                    if (callback !== undefined) {
                        callback();
                    }
                }
            });
        };
        _this.onSampleTypeStateChange = function (sampleTypeFormState, _, callback) {
            return _this.onSampleTypeStateChangeBaseHandler(sampleTypeFormState, callback);
        };
        _this.onProbeEnableChange = function () {
            _this.props.setFormState(function (s) {
                return { probeEnabled: !s.probeEnabled };
            }, function (newState) {
                var _a;
                if (newState.probeEnabled) {
                    // trigger a state change so that the amplifier is overwritten
                    _this.onProbeStateChange({}, (_a = _this.props.formState.probeState.probeAmplifier) === null || _a === void 0 ? void 0 : _a.amplifier, _this.onUserSelection);
                }
                else {
                    _this.onUserSelection();
                }
            });
        };
        _this.onAmplifierEnableChange = function () {
            return _this.props.setFormState(function (s) { return ({ ampEnabled: !s.ampEnabled }); }, _this.onUserSelection);
        };
        _this.onBuffersEnableChange = function () {
            return _this.props.setFormState(function (s) { return ({ buffersEnabled: !s.buffersEnabled }); }, _this.onUserSelection);
        };
        _this.onCommentChange = function (event) {
            var str = event.currentTarget.value;
            _this.props.setFormState(function () {
                return {
                    comment: str
                };
            });
        };
        // The user finished editing something
        _this.onUserSelection = function () {
            _this.loadPrice();
        };
        // TODO dedup w/ rna-fish
        _this.loadPrice = function () {
            _this.props.setFormState(function () { return ({
                priceLoading: true
            }); }, function (newState) {
                postJsonWithAuthn(_this.props.kitPriceUrlPath, JSON.stringify(serializeSecondaryIfKit(newState)))
                    .then(function (resp) {
                    if (resp.status === 400) {
                        return resp.json().then(function (json) { return new ErrorElementsWithPrices(json.errors, json.price); });
                    }
                    return resp.json();
                }).then(function (resp) {
                    if (resp instanceof ErrorElementsWithPrices) {
                        _this.props.setFormState(function () { return ({
                            errors: resp.errors,
                            priceLoading: false,
                            price: resp.price
                        }); });
                    }
                    else {
                        _this.props.setFormState(function () { return ({
                            errors: [],
                            price: resp,
                            priceLoading: false
                        }); });
                    }
                }).catch(function (rej) {
                    console.error("Couldn't update price: " + rej);
                });
            });
        };
        return _this;
    }
    EditKitForm.prototype.render = function () {
        return React.createElement("div", { className: 'kit-form' },
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement("div", null),
            React.createElement(Header, { className: "component-slider" },
                React.createElement("span", { className: 'sample-type-span' }, "Sample Type")),
            React.createElement("div", { className: 'no-outline-kit-component' },
                React.createElement(SelectSampleTypeForm, { sampleTypeState: this.props.formState.sampleTypeFormState, sampleTypes: this.props.catalog.buffer.sampleTypes, manualPro: false, onSampleTypeChange: this.onSampleTypeStateChange, onUserSelection: this.onUserSelection, errors: this.props.formState.errors, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.probeEnabled, label: this.props.legacy ? 'HCR™ Probe' : "HCR™ HiFi 2˚ Antibody Probe", otherComponentStates: [this.props.formState.ampEnabled, this.props.formState.buffersEnabled], onChange: this.onProbeEnableChange, cish: false },
                React.createElement(ProbeForm, { catalog: this.props.catalog, errors: this.props.formState.errors, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.probePriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.probePriceCents : undefined, priceLoading: this.props.formState.priceLoading, probeState: this.props.formState.probeState, amplifierDropdownDisabled: this.props.formState.ampEnabled, enableCustomPrice: this.props.enableCustomPrice, onProbeChange: this.onProbeStateChange, onUserSelection: this.onUserSelection, legacy: this.props.legacy, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.ampEnabled, label: this.props.legacy ? 'HCR™ Amplifier (v3.0)' : 'HCR™ Gold Amplifier', otherComponentStates: [this.props.formState.probeEnabled, this.props.formState.buffersEnabled], onChange: this.onAmplifierEnableChange, cish: false },
                React.createElement(HcrAmplifierForm, { amplifierLabel: this.props.legacy ? EditKitForm.amplifierLabel : EditKitForm.goldAmplifierLabel, ampState: this.props.formState.ampState, catalog: this.ampCatalogForSelectedProbe(), errors: this.props.formState.errors, enableCustomPrice: this.props.enableCustomPrice, enableCustomScale: false, enableAdminSystems: false, onAmpStateChange: this.onAmpStateChange, selectedSampleType: this.props.formState.sampleTypeFormState.sampleType, onUserSelection: this.onUserSelection, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.amplifierPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.amplifierPriceCents : undefined, priceLoading: this.props.formState.priceLoading, experimentVolumeTable: this.props.legacy ? ProbeForm.experimentVolumeTable : EditKitForm.goldExperimentTable, legacy: this.props.legacy, encoder: false, secondaryIf: true, showErrors: this.props.showErrors })),
            React.createElement(ToggleableKitComponent, { componentEnabled: this.props.formState.buffersEnabled, label: 'HCR™ Buffers', otherComponentStates: [this.props.formState.probeEnabled, this.props.formState.ampEnabled], onChange: this.onBuffersEnableChange, cish: false },
                React.createElement(BufferForm, { bufferState: this.props.formState.buffersState, catalog: this.props.catalog.buffer, errors: this.props.formState.errors, onBufferChange: this.onBufferStateChange, onUserSelection: this.onUserSelection, priceLoading: this.props.formState.priceLoading, effectivePriceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.buffersPriceCents : undefined, listPriceCents: this.props.formState.price ? this.props.formState.price.listPrice.buffersPriceCents : undefined, enableCustomPrice: this.props.enableCustomPrice, legacy: this.props.legacy, showErrors: this.props.showErrors })),
            React.createElement(ErrorDisplay, { errors: this.props.formState.errors, errorPath: "/" }),
            React.createElement(Grid, { columns: 4, stackable: true },
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 12, className: "no-left-padding-mobile" },
                        React.createElement(Form.Field, null,
                            React.createElement("label", { htmlFor: 'comment' }, "Comment"),
                            React.createElement(Form.TextArea, { style: { fontFamily: 'Avenir Next' }, placeholder: 'Please describe any instructions for order fulfillment or ' +
                                    'anything else the MI Team should be aware of regarding this order.', value: this.props.formState.comment, onChange: this.onCommentChange }))),
                    priceColumn(React.createElement("div", { 
                        // apply padding that matches the kit component div padding
                        style: { 'paddingRight': '.5rem' } },
                        React.createElement(PriceDisplay, { loading: this.props.formState.priceLoading, priceCents: this.props.formState.price ? this.props.formState.price.effectivePrice.subtotalCents : undefined })))),
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 16 }, this.props.formState.price && discountMessage(this.props.formState.price.discountCents, this.props.formState.price.listPrice.subtotalCents)))));
    };
    EditKitForm.prototype.componentDidMount = function () {
        if (this.props.loadPriceOnMount) {
            this.loadPrice();
        }
    };
    EditKitForm.amplifierLabel = React.createElement(React.Fragment, null,
        "Amplifier ",
        React.createElement(Help, null, "For multiplex experiments, choose a different HCR\u2122 Amplifier (v3.0) (B1, B2, ...) for each target that will be imaged in the same sample (for example, amplifier B1 for target 1, amplifier B2 for target 2, ...). Note that if your kit includes an HCR\u2122 Probe, this selection also sets the probe\u2019s \u201CFor use with amplifier\u201D above."));
    EditKitForm.goldAmplifierLabel = React.createElement(React.Fragment, null,
        "Amplifier ",
        React.createElement(Help, null,
            React.createElement("p", null, "For multiplex experiments, choose a different HCR\u2122 Gold Amplifier (X1, X2, \u2026) for each target that will be imaged in the same sample (for example, amplifier X1 for target 1, amplifier X2 for target 2, \u2026). Note that if your kit includes an HCR\u2122 Probe, this selection also sets the probe\u2019s \u201CFor use with amplifier\u201D above."),
            React.createElement("p", null, "For flexibility, 488, 546, 594, and 647 are available across all X1-X10 systems. For expanded channel options, select amplifiers also offer an additional unique label option. We recommend using a longer-wavelength label (e.g., 647 or 750) for targets that are more difficult to detect (e.g., low-expression targets), as autofluorescence tends to be higher in shorter-wavelength channels (e.g., 405 or 488).")));
    EditKitForm.goldExperimentTable = React.createElement(Table, { celled: true, striped: true },
        React.createElement(Table.Header, null,
            React.createElement(Table.Row, null,
                React.createElement(Table.HeaderCell, null, "Sample type"),
                React.createElement(Table.HeaderCell, null, "Reaction volume (\u00B5L)"),
                React.createElement(Table.HeaderCell, null, "HCR\u2122 Gold Amplifier (\u00B5L)"),
                React.createElement(Table.HeaderCell, null, "Experiments"))),
        React.createElement(Table.Body, null,
            React.createElement(Table.Row, null,
                React.createElement(Table.Cell, null, "Sample on a slide (e.g., tissue section or adherent cells)"),
                React.createElement(Table.Cell, null, "100"),
                React.createElement(Table.Cell, null, "2"),
                React.createElement(Table.Cell, null, "100")),
            React.createElement(Table.Row, null,
                React.createElement(Table.Cell, null, "Sample in solution (e.g., whole-mount embryo or cells in suspension)"),
                React.createElement(Table.Cell, null, "200"),
                React.createElement(Table.Cell, null, "4"),
                React.createElement(Table.Cell, null, "50"))));
    return EditKitForm;
}(React.PureComponent));
export { EditKitForm };
export var defaultSecondaryIfFormState = function (catalog, legacy) { return ({
    ampEnabled: true,
    ampState: defaultAmpState(getSecondaryIfAmplifierCatalog(legacy, catalog)),
    buffersEnabled: true,
    buffersState: defaultBuffersState(catalog, legacy),
    comment: '',
    errors: [],
    probeEnabled: true,
    probeState: defaultProbeState(getSecondaryIfProbeCatalog(legacy, catalog)),
    sampleTypeFormState: defaultSampleTypeState(),
    priceLoading: false,
    legacy: legacy
}); };
// TODO tests for serialization
export var serializeSecondaryIfKit = function (state) {
    var kit = {
        comment: state.comment,
        priceModes: {
            probeMode: serializePriceMode(state.probeEnabled ? state.probeState.customPrice : undefined),
            amplifierMode: serializePriceMode(state.ampEnabled ? state.ampState.customPrice : undefined),
            buffersMode: serializePriceMode(state.buffersEnabled ? state.buffersState.customPrice : undefined)
        }
    };
    if (state.probeEnabled) {
        kit.probeComponent = serializeProbe(state.probeState);
    }
    if (state.ampEnabled) {
        // re-use the rna-fish serialization since it's the same component
        kit.amplifierComponent = KitSerializer.serializeAmplifier(state.ampState);
    }
    if (state.buffersEnabled) {
        kit.buffersComponent = serializeBuffers(state.buffersState, state.legacy);
    }
    kit.legacy = state.legacy;
    kit.sampleTypeId = state.sampleTypeFormState.sampleType.id;
    kit.starterKit = false;
    return {
        '_type': '2-ihc',
        kit: kit
    };
};
var ProbeStateWrangler = /** @class */ (function () {
    function ProbeStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ probeState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.probeState; };
    }
    return ProbeStateWrangler;
}());
var AmpStateWrangler = /** @class */ (function () {
    function AmpStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ ampState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.ampState; };
    }
    return AmpStateWrangler;
}());
var BufferStateWrangler = /** @class */ (function () {
    function BufferStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ buffersState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.buffersState; };
    }
    return BufferStateWrangler;
}());
var SampleTypeStateWrangler = /** @class */ (function () {
    function SampleTypeStateWrangler() {
        this.buildHolderFromComponentState = function (componentFormState) { return ({ sampleTypeFormState: componentFormState }); };
        this.extractComponentState = function (holder) { return holder.sampleTypeFormState; };
    }
    return SampleTypeStateWrangler;
}());
var probeStateWrangler = new ProbeStateWrangler();
var ampStateWrangler = new AmpStateWrangler();
var bufStateWrangler = new BufferStateWrangler();
var sampleTypeStateWrangler = new SampleTypeStateWrangler();
//# sourceMappingURL=EditKitForm.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/secondary-if/EditKitForm.js
// module id = 103
// module chunks = 0